import d3 = require("d3");
import React = require("react");
import ReactFauxDOM = require("react-faux-dom");
import {PrettyPrinter} from "../../../components/Utils/PrettyPrinter";
import {MarketingTabMapper} from "../utils/MarketingTabMapper";
import "./Chart.scss";
import {removeNaN} from "../../../Utils/Utils";

export interface LabelOptions {
    externalLegend: boolean;
}

interface BarChartGroupElement {
    name: string;
    color: string;
    value: number;
}

interface BarChartGroup {
    id?: number;
    name: string;
    elements: BarChartGroupElement[];
}

interface HorizontalGroupBarChartProps {
    width: number;
    height: number;
    color: string;
    data: BarChartGroup[];
    xUnitOfMeasure: string;
    onYFieldsClick?: Function;
    labelsOptions?: LabelOptions;

    selectedItem?: string;

    onPathOver?: Function;
}

interface HorizontalGroupBarChartState {
    selectedItem: string;
}

export class HorizontalGroupBarChart extends React.Component<HorizontalGroupBarChartProps, HorizontalGroupBarChartState> {

    private static DEFAULT_COLOR = "var(--vebox-gray-hover-light)";
    /** The color of histogram bars. */
    private static BAR_COLOR: string = "#75ca7e";

    private randomElementsPrefix: string;

    constructor(props: HorizontalGroupBarChartProps) {
        super(props);
        this.state = {selectedItem: null};
        this.setSelectedItem = this.setSelectedItem.bind(this);
        this.randomElementsPrefix = Math.random()
            .toString(36)
            .replace(/[^a-z]+/g, "")
            .substr(0, 10);
        this.mouseOut = this.mouseOut.bind(this);
        this.mouseOver = this.mouseOver.bind(this);
    }

    public setSelectedItem(itemName: string): void {
        this.setState({selectedItem: itemName});
    }

    public render() {
        // Create the element
        const data = this.props.data;
        const props = this.props;
        if (data.length == 0) {
            return null;
        }
        const div = new ReactFauxDOM.Element("div");
        let color = HorizontalGroupBarChart.BAR_COLOR;
        if (this.props.color && this.props.color != "") {
            color = this.props.color;
        }
        const margin = {top: 0, right: 100, bottom: 10, left: 40},
            width = removeNaN(this.props.width - margin.left - margin.right, 0),
            height = this.props.height - margin.top - margin.bottom - 80;
        const barSpacePercentage = 0.35;
        const barHeight = ((height - 100) / data.length) / (1 + barSpacePercentage);
        const defBarSpace = (height / data.length) * barSpacePercentage;

        // create svg for histogram.
        const gWidth = this.props.width;
        const svg = d3.select(div).append("svg")
            .attr("width", (gWidth))
            .attr("height", this.props.height)
            .append("g")
            .attr("transform", `translate(${margin.left}, 30)`);

        const y = d3.scaleBand()
            .rangeRound([height, 0])
            .domain(data.map(function (d: BarChartGroup) {
                    return d.name;
                }),
            );
        // Create function for x-axis map.
        const x = d3.scaleLinear()
            .range([0, width])
            .domain(
                [
                    0,
                    d3.max(data, function (d: BarChartGroup) {
                        return d3.max(d.elements, function (d1: BarChartGroupElement) {
                            return d1.value;
                        });
                    }),
                ],
            );
        // Add x-axis to the histogram svg.
        svg.append("g")
            .attr("class", "x axis")
            .style("display", "none")
            .attr("transform", `translate(0,${height})`)
            .call((d3 as any).axisBottom().scale(x))
            .selectAll("text")
            .attr("data-id", function (d, i) {
                return "dDateText" + i;
            })
            .attr("class", "dateFieldsClass")
            .style("font-size", "10px")
            .style("fill", "black")
            .style("text-anchor", "middle");
        const local = d3.local();
        const yAxisLeft = (d3 as any).axisRight()
            .scale(y)
            .tickFormat(function (d, i) {
                local.set(this, data[i].id);
                return d;
            })
            .ticks([5])
            .tickSize(width);
        const customAxis = function (g) {
            g.selectAll("text")
                .attr("x", -40)
                .attr("dy", -barHeight + defBarSpace - 5 - (100 / data.length))
                .style("fill", "black")
                .style("font-size", "12px");
        };
        const yAxisGroup = svg.append("g")
            .attr("class", "y axis")
            .style("fill", "grey")
            .call(yAxisLeft)
            .call(customAxis);
        svg.selectAll("g.y.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.x.axis").selectAll("path").style("display", "none");
        svg.selectAll("g.y.axis").selectAll("line").style("display", "none");
        svg.selectAll("g.x.axis").selectAll("line").style("display", "none");
        x.domain([0, d3.max(data, function (d: BarChartGroup) {
            return d3.max(d.elements, function (d1: BarChartGroupElement) {
                return d1.value;
            });
        })]);
        // Create bars for histogram to contain rectangles and freq labels.
        const bars = svg.append("g").attr("class", "bar");
        // create the rectangles.
        const defSpace = 5;

        let maxValue = 0;
        data.forEach((group: BarChartGroup) => {
            group.elements.forEach((elm) => {
                if (elm.value > maxValue) {
                    maxValue = elm.value
                }
            })
        })
        const zeroValue = maxValue * 0.005;

        data.forEach((group: BarChartGroup) => {
            let i = 0;
            group.elements.forEach((element: BarChartGroupElement) => {
                    const intBarHeight = (barHeight / group.elements.length) - defSpace;
                    const space = (i != 0) ? defSpace : 0;
                    const shift = defBarSpace / 2 - defSpace;
                    const barY = shift + y(group.name) + (intBarHeight + space) * i;
                    bars.append("rect")
                        .attr("x", 0)
                        .attr("y", barY)
                        .attr("height", intBarHeight)
                        .attr("data-id", this.randomElementsPrefix + "histogram-bar-" + MarketingTabMapper.codifyEntityName(
                            element.name))
                        .attr("width", x(element.value > zeroValue ? element.value : zeroValue))
                        .attr("fill", (element.color && element.color != "") ? element.color : color)
                        .style("opacity", ".8")
                        .classed(this.randomElementsPrefix + "bars", true)
                        .on("mouseover", (event) => {
                            this.mouseOver(event, element);
                        })
                        .on("mouseout", () => {
                            this.mouseOut();
                        })
                        .style("cursor", "pointer");

                    // Create the frequency labels above the rectangles.
                    bars.append("text")
                        .text(element.value != null ? PrettyPrinter.salesQuantity(
                            element.value) + " " + this.props.xUnitOfMeasure : "")
                        .attr(
                            "data-id",
                            this.randomElementsPrefix + "element-frequency-" + MarketingTabMapper.codifyEntityName(
                                element.name),
                        )
                        .attr("y", barY + intBarHeight / 2 + 5)
                        .attr("x", x(element.value > zeroValue ? element.value : zeroValue) + 5)
                        .attr("text-anchor", "right")
                        .classed("bars-text-field", true)
                        .classed(this.randomElementsPrefix + "bar-frequencies", true)
                        .style("font-size", "12px");
                    i++;
                },
            );
        });

        if (!this.props?.labelsOptions || !this.props?.labelsOptions?.externalLegend) {
            if (data.length > 0 && data[0].elements.length > 0) {
                let dataL = 0;
                const offset = width / data[0].elements.length;
                const legend = svg.selectAll(".legends4")
                    .data(data[0].elements)
                    .enter()
                    .append("g")
                    .attr("data-id", (d) => {
                            return this.randomElementsPrefix + "legend-element-" + MarketingTabMapper.codifyEntityName(
                                d.name);
                        },
                    )
                    .classed(this.randomElementsPrefix + "legend-elements", true)
                    .classed("legends4", true)
                    .attr("transform", function (d, i) {
                        if (i === 0) {
                            dataL = data[0].elements.length + offset;
                            return "translate(0," + (height + 20) + ")";
                        } else {
                            const newdataL = dataL;
                            dataL += data[0].elements.length + offset;
                            return "translate(" + (newdataL) + "," + (height + 20) + ")";
                        }
                    }).on("mouseover", this.mouseOver)
                    .on("mouseout", () => {
                        this.mouseOut();
                    })
                    .style("cursor", "pointer");

                legend.append("rect")
                    .attr("x", 0)
                    .attr("y", 0)
                    .attr("width", 10)
                    .attr("height", 10)
                    .style("fill", function (d) {
                        return d.color;
                    });

                legend.append("text")
                    .attr("x", 12)
                    .attr("y", 10)
                    // .attr("dy", ".35em")
                    .text(function (d) {
                        return d.name;
                    })
                    .attr("class", "textselected")
                    .style("text-anchor", "start")
                    .style("font-size", 12);
            }
        }

        if (this.props?.labelsOptions?.externalLegend) {
            if (this.props.selectedItem != null) {
                this.colorSelectedBars(this.props.selectedItem)
            } else {
                this.colorBars()
            }
        } else {
            if (this.state.selectedItem != null) {
                this.colorSelectedBars(this.state.selectedItem)
            } else {
                this.colorBars()
            }
        }
        const normalizeText = (text: string): string => {
            if (text.length > 60) {
                text = text.slice(0, 60) + "...";
            }
            return text;
        };
        svg.selectAll("g.y.axis").selectAll("text").each(function (d, i) {
            d3.select(this).text(normalizeText(d3.select(this).text()));
        });
        const getEntityId: Function = (entityName) => {
            for (let i = 0; i < data.length; i++) {
                const element: BarChartGroup = data[i];
                if (element.name == entityName) {
                    return element.id;
                }
            }
            return null;
        };

        yAxisGroup.selectAll("text")
            .style("cursor", (props.onYFieldsClick != null) ? "pointer" : "")
            .each(function () {
                d3.select(this).on("click", function () {
                    if (props.onYFieldsClick) {
                        const entityName: any = d3.select(this).data()[0];
                        const entityId: number = getEntityId(entityName);
                        if (entityId == null) {
                            console.error("entityId with name " + entityName + " not found in data alements ");
                            return;
                        }
                        props.onYFieldsClick(entityId);
                    }
                });
            });
        return div.toReact();
    }

    public mouseOver(event, itemData: BarChartGroupElement) {
        if (this.props?.labelsOptions?.externalLegend) {
            this.props?.onPathOver(itemData.name);
        }
        this.setSelectedItem(itemData.name);
    }

    public mouseOut() {
        if (this.props?.labelsOptions?.externalLegend) {
            this.props?.onPathOver(null);
        }
        this.setSelectedItem(null);
    }

    public colorSelectedBars(item: string) {
        //Color of frequencies number
        d3.selectAll("." + this.randomElementsPrefix + "bar-frequencies")
            .style("opacity", "0");
        d3.selectAll(
            "[data-id=" + this.randomElementsPrefix + "element-frequency-" + MarketingTabMapper.codifyEntityName(
                item) + "]")
            .style("opacity", "1");
        //Color of bars
        d3.selectAll("." + this.randomElementsPrefix + "bars")
            .style("opacity", ".8");
        d3.selectAll("[data-id=" + this.randomElementsPrefix + "histogram-bar-" + MarketingTabMapper.codifyEntityName(
            item) + "]")
            .style("opacity", ".4")
        //Color of legend
        if (!this.props?.labelsOptions?.externalLegend) {
            d3.selectAll("." + this.randomElementsPrefix + "legend-elements")
                .style("opacity", ".2");
            d3.selectAll(
                "[data-id=" + this.randomElementsPrefix + "legend-element-" + MarketingTabMapper.codifyEntityName(
                    item) + "]")
                .style("opacity", "1");
        }
    }

    public colorBars() {
        d3.selectAll("." + this.randomElementsPrefix + "bars")
            .style("opacity", ".8");
        d3.selectAll("." + this.randomElementsPrefix + "bar-frequencies")
            .style("opacity", "1");
        if (!this.props.labelsOptions.externalLegend) {
            d3.selectAll("." + this.randomElementsPrefix + "legend-elements")
                .style("opacity", "1");
        }
    }
}

